import React, { useState } from 'react';
import {
    LayoutGrid,
    Globe,
    Construction,
    CreditCard,
    Mail,
    Store,
    ClipboardList,
    Zap,
    ChevronRight,
    Lock,
    GraduationCap,
    Loader2,
    CheckCircle2,
    AlertCircle,
} from 'lucide-react';
import { useBilling } from '../context/BillingContext';
import { moduleService } from '../services/modules';
import { isPartner } from '../utils/roleUtils';
import { WebsiteBuilderApp } from "./ModulesView/apps/WebsiteBuilderApp";
import { DomainsApp } from "./ModulesView/apps/DomainsApp";
import { EmailApp } from "./ModulesView/apps/EmailApp";
import { GBPApp } from "./ModulesView/apps/GBPApp";
import { BizBuildingApp } from "./ModulesView/apps/BizBuildingApp";

const statusIcon = {
    running: Loader2,
    completed: CheckCircle2,
    failed: AlertCircle,
    idle: null,
};

const statusColor = {
    running: 'text-blue-500',
    completed: 'text-emerald-500',
    failed: 'text-red-500',
    idle: '',
};

const ModulesView = ({ onSelectModule, onModuleExecuted, userRole }) => {
    const { hasAccess } = useBilling();
    const [activePortal, setActivePortal] = useState<string | null>(null);
    const [executingModule, setExecutingModule] = useState<string | null>(null);
    const [moduleResults, setModuleResults] = useState<Record<string, { status: string; error?: string }>>({});

    const modules = [
        {
            id: 'bizbuilding',
            label: 'BizBuilding Program',
            description: 'AI-driven business development framework',
            icon: GraduationCap,
            color: 'bg-amber-100 text-amber-600',
            roles: ['ADMIN', 'COACH', 'PARTNER', 'CLIENT'],
            premium: false,
        },
        {
            id: 'domain_manager',
            label: 'Domains',
            description: 'Search and manage digital real estate',
            icon: Globe,
            color: 'bg-blue-100 text-blue-600',
            roles: ['ADMIN', 'COACH', 'PARTNER'],
            premium: true,
        },
        {
            id: 'website_builder',
            label: 'WebsiteBuilder',
            description: 'AI-powered website generation',
            icon: Construction,
            color: 'bg-purple-100 text-purple-600',
            roles: ['ADMIN', 'COACH', 'PARTNER'],
            premium: true,
        },
        {
            id: 'email_setup',
            label: 'Email Service',
            description: 'Professional alias & SMTP setup',
            icon: Mail,
            color: 'bg-indigo-100 text-indigo-600',
            roles: ['ADMIN', 'COACH', 'PARTNER'],
            premium: true,
        },
        {
            id: 'gbp_opt',
            label: 'Google Business',
            description: 'Local SEO & Profile Management',
            icon: Store,
            color: 'bg-rose-100 text-rose-600',
            roles: ['ADMIN', 'COACH', 'PARTNER'],
            premium: true,
        },
    ];

    const filteredModules = modules.filter(m => {
        if (userRole === 'ADMIN') {return true;}
        const hasRole = m.roles.includes(userRole) || (m.roles.includes('PARTNER') && isPartner(userRole));
        const isPremium = m.premium;
        return hasRole && (!isPremium || hasAccess(m.id));
    });

    const seedWorkflows = async () => {
        try {
            const res = await fetch('/api/admin/seed-workflows', { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                alert('Workflows seeded successfully! You can now execute modules.');
            } else {
                alert(`Seeding failed: ${data.error}`);
            }
        } catch (err: any) {
            alert(`Seeding error: ${err.message}`);
        }
    };

    const handleExecutionStart = (executionId: string) => {
        if (onModuleExecuted) {
            onModuleExecuted({
                moduleId: activePortal,
                executionId
            });
        }
        setActivePortal(null);
    };

    const handleModuleClick = (moduleId: string) => {
        setActivePortal(moduleId);
    };

    const renderPortal = () => {
        if (!activePortal) {return null;}

        switch (activePortal) {
            case 'domain_manager':
                return <DomainsApp onExecutionStarted={handleExecutionStart} onBack={() => setActivePortal(null)} />;
            case 'website_builder':
                return <WebsiteBuilderApp onExecutionStarted={handleExecutionStart} onBack={() => setActivePortal(null)} />;
            case 'email_setup':
                return <EmailApp onExecutionStarted={handleExecutionStart} onBack={() => setActivePortal(null)} />;
            case 'gbp_opt':
                return <GBPApp onExecutionStarted={handleExecutionStart} onBack={() => setActivePortal(null)} />;
            case 'bizbuilding':
                return <BizBuildingApp onExecutionStarted={handleExecutionStart} onBack={() => setActivePortal(null)} />;
            default:
                return null;
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {activePortal ? (
                renderPortal()
            ) : (
                <>
                    <div className="flex items-center justify-between gap-4 mb-2">
                        <div className="flex items-center gap-4">
                            <div className="bg-slate-900 p-3 rounded-2xl text-white">
                                <LayoutGrid size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Modules Hub</h2>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-1">
                                    Select a specialized tool to continue
                                </p>
                            </div>
                        </div>
                        {userRole === 'ADMIN' && (
                            <button
                                onClick={seedWorkflows}
                                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                            >
                                Seed Workflows
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {filteredModules.map((m) => {
                            const isLocked = m.premium && !hasAccess(m.id);
                            const isExecuting = executingModule === m.id;
                            const result = moduleResults[m.id];
                            const StatusIcon = result ? statusIcon[result.status as keyof typeof statusIcon] : null;

                            return (
                                <button
                                    key={m.id}
                                    onClick={() => !isLocked && handleModuleClick(m.id)}
                                    disabled={isExecuting}
                                    className={`group flex items-center gap-5 p-6 rounded-[2.5rem] border transition-all text-left relative overflow-hidden active:scale-[0.98] ${isLocked
                                        ? 'bg-slate-50 border-slate-200 opacity-80 cursor-not-allowed'
                                        : isExecuting
                                            ? 'bg-blue-50 border-blue-200 cursor-wait'
                                            : 'bg-white border-slate-100 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 cursor-pointer'
                                        }`}
                                >
                                    <div className={`p-4 rounded-3xl ${m.color} shadow-inner transition-transform ${!isLocked && !isExecuting && 'group-hover:scale-110'}`}>
                                        {isExecuting ? (
                                            <Loader2 size={24} className="animate-spin" />
                                        ) : (
                                            <m.icon size={24} />
                                        )}
                                    </div>

                                    <div className="flex-1">
                                        <h3 className={`text-lg font-black uppercase tracking-tight transition-colors ${isLocked ? 'text-slate-400' : 'text-slate-800 group-hover:text-blue-700'
                                            }`}>
                                            {m.label}
                                        </h3>
                                        <p className="text-slate-400 text-xs font-medium leading-relaxed">
                                            {m.description}
                                        </p>
                                        {result && result.status !== 'idle' && (
                                            <div className={`flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider ${statusColor[result.status as keyof typeof statusColor]}`}>
                                                {StatusIcon && <StatusIcon size={12} className={result.status === 'running' ? 'animate-spin' : ''} />}
                                                {result.status === 'failed' ? result.error ?? 'Failed' : result.status}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`${isLocked ? 'text-slate-300' : 'text-slate-200 group-hover:text-blue-600'} transition-colors`}>
                                        {isLocked ? <Lock size={20} /> : <ChevronRight size={20} />}
                                    </div>

                                    <div className="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
                                        <m.icon size={100} />
                                    </div>

                                    {isLocked && (
                                        <div className="absolute top-4 right-6">
                                            <span className="bg-slate-200 text-slate-500 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">
                                                Premium
                                            </span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="py-8 text-center">
                        <p className="text-[10px] text-slate-300 font-black uppercase tracking-[0.2 laem]">
                            BizBuilder Enterprise Suite
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default ModulesView;
